// FICHIER : frontend/src/pages/Candidats.js
import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import NavLogo from '../components/NavLogo';
import axios from "axios";

const API = "https://ark-recruitment-backend.onrender.com/api";
const CACHE_KEY = "ark_interview_session";

const PHASES = ["profilage", "comportementale", "technique", "cognitive"];
const PHASE_LABELS = {
  profilage: "Profilage",
  comportementale: "Comportemental",
  technique: "Technique",
  cognitive: "Cognitif",
};

function sauvegarderSession(data) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(data)); } catch (e) {}
}
function chargerSession() {
  try { return JSON.parse(localStorage.getItem(CACHE_KEY)); } catch (e) { return null; }
}
function supprimerSession() {
  try { localStorage.removeItem(CACHE_KEY); } catch (e) {}
}

export default function Candidat() {
  const navigate = useNavigate();
  const location = useLocation();
  const jobDescription = location.state?.job_description || "Poste non spécifié";
  const [view, setView]                           = useState("upload");
  const [cvMode, setCvMode]                       = useState(null);
  const [file, setFile]                           = useState(null);
  const [loading, setLoading]                     = useState(false);
  const [error, setError]                         = useState(null);
  const [cvText, setCvText]                       = useState("");
  const [cvAnalysis, setCvAnalysis]               = useState(null);
  const [analyseId, setAnalyseId]                 = useState(null);
  const [filename, setFilename]                   = useState("");
  const [phase, setPhase]                         = useState("profilage");
  const [historique, setHistorique]               = useState([]);
  const [historiqueComplet, setHistoriqueComplet] = useState({});
  const [phasesResults, setPhasesResults]         = useState({});
  const [messages, setMessages]                   = useState([]);
  const [reponse, setReponse]                     = useState("");
  const [phaseComplete, setPhaseComplete]         = useState(false);
  const [questionEnCours, setQuestionEnCours]     = useState("");
  const [sessionExistante, setSessionExistante]   = useState(false);
  const [isMobile, setIsMobile]                   = useState(window.innerWidth < 768);

  const [experience,  setExperience]  = useState('');
  const [formation,   setFormation]   = useState('');
  const [competences, setCompetences] = useState('');
  const [expPro,      setExpPro]      = useState('');

  const chatRef = useRef(null);

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  useEffect(() => {
    const session = chargerSession();
    if (session && session.view === "interview") setSessionExistante(true);
  }, []);

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [messages]);

  useEffect(() => {
    if (view === "interview") {
      sauvegarderSession({
        view, cvText, cvAnalysis, phase, historique,
        historiqueComplet, phasesResults, messages,
        phaseComplete, questionEnCours, analyseId, filename
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, phase, historique, historiqueComplet, messages, phaseComplete, phasesResults]);

  const reprendreSession = () => {
    const session = chargerSession();
    if (!session) return;
    setCvText(session.cvText || "");
    setCvAnalysis(session.cvAnalysis || null);
    setPhase(session.phase || "profilage");
    setHistorique(session.historique || []);
    setHistoriqueComplet(session.historiqueComplet || {});
    setPhasesResults(session.phasesResults || {});
    setMessages(session.messages || []);
    setPhaseComplete(session.phaseComplete || false);
    setQuestionEnCours(session.questionEnCours || "");
    setAnalyseId(session.analyseId || null);
    setFilename(session.filename || "");
    setView("interview");
    setSessionExistante(false);
  };

  const ignorerSession = () => { supprimerSession(); setSessionExistante(false); };

  const handleUpload = async () => {
    setLoading(true);
    setError(null);

    let cvTexte = "";
    if (cvMode === 'create') {
      cvTexte = `Expérience: ${experience}\nFormation: ${formation}\nCompétences: ${competences}\nExpérience professionnelle: ${expPro}`;
    }

    const formData = new FormData();
    if (cvMode === 'upload' && file) formData.append("file", file);
    formData.append("job_description", jobDescription);
    if (cvMode === 'create') formData.append("cv_text_override", cvTexte);

    try {
      const res = await axios.post(`${API}/upload-cv`, formData);
      const cvTextResult = cvMode === 'create' ? cvTexte : (res.data.cv_text || "");
      setCvText(cvTextResult);
      setCvAnalysis(res.data.analyse);
      setAnalyseId(res.data.analyse_id);
      setFilename(file ? file.name : "cv_cree.txt");
      setView("interview");
      setPhase("profilage");
      setHistorique([]);
      setHistoriqueComplet({});
      setPhasesResults({});
      setMessages([]);
      setPhaseComplete(false);
      await chargerQuestion("profilage", [], cvTextResult);
    } catch (e) {
      setError("Erreur lors de l'analyse. Vérifiez que le backend est démarré.");
    } finally {
      setLoading(false);
    }
  };

  const chargerQuestion = async (phaseActuelle, historiqueActuel, cvTextOverride = null) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/interview/question`, {
        cv_text: cvTextOverride || cvText,
        job_description: jobDescription,
        phase: phaseActuelle,
        historique: historiqueActuel,
      });
      if (res.data.est_complete) {
        setPhaseComplete(true);
      } else {
        setQuestionEnCours(res.data.question);
        setMessages(prev => [...prev, { role:"ia", content:res.data.question }]);
      }
    } catch (e) {
      setError("Erreur lors du chargement de la question.");
    } finally {
      setLoading(false);
    }
  };

  const envoyerReponse = async () => {
    if (!reponse.trim()) return;
    const nouvelHistorique = [...historique, { question:questionEnCours, reponse }];
    setHistorique(nouvelHistorique);
    setMessages(prev => [...prev, { role:"user", content:reponse }]);
    setReponse("");
    await chargerQuestion(phase, nouvelHistorique);
  };

  const evaluerPhase = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/interview/evaluate`, {
        cv_text: cvText,
        job_description: jobDescription,
        phase,
        historique,
        cv_analysis: cvAnalysis,
        phases_results: phasesResults,
      });
      const newResults           = { ...phasesResults, [phase]: res.data.evaluation };
      const newHistoriqueComplet = { ...historiqueComplet, [phase]: historique };
      setHistoriqueComplet(newHistoriqueComplet);
      setPhasesResults(newResults);
      if (res.data.toutes_phases_completes) {
        await genererRapport(newResults, newHistoriqueComplet);
      } else {
        const nextPhase = res.data.next_phase;
        setPhase(nextPhase);
        setHistorique([]);
        setPhaseComplete(false);
        setMessages([]);
        await chargerQuestion(nextPhase, []);
      }
    } catch (e) {
      setError("Erreur lors de l'évaluation.");
    } finally {
      setLoading(false);
    }
  };

  const genererRapport = async (results, histComplet) => {
    setLoading(true);
    try {
      await axios.post(`${API}/interview/rapport`, {
        cv_text: cvText,
        job_description: jobDescription,
        phase: "cognitive",
        historique,
        cv_analysis: cvAnalysis,
        phases_results: results,
        analyse_id: analyseId,
        filename,
        historique_complet: histComplet,
      });
      supprimerSession();
      setView("merci");
    } catch (e) {
      setError("Erreur lors de la génération du rapport.");
    } finally {
      setLoading(false);
    }
  };

  const phaseIndex = PHASES.indexOf(phase);
  const progression = Math.round(((phaseIndex * 5 + historique.length) / 20) * 100);
  const questionNum = Math.min(historique.length + 1, 5);

  const canStart = cvMode === 'upload' ? !!file :
                   cvMode === 'create' ? (!!experience && !!formation && !!competences.trim() && !!expPro.trim()) :
                   false;

  const fieldLabel = {
    fontFamily:'DM Mono,monospace', fontSize:10, color:'var(--muted)',
    letterSpacing:2, textTransform:'uppercase', marginBottom:8, display:'block',
  };
  const fieldInput = {
    width:'100%', background:'transparent', border:'none',
    borderBottom:'1px solid var(--border)', padding:'10px 0',
    fontSize:14, color:'var(--text)', fontFamily:'DM Sans,sans-serif', outline:'none',
  };
  const selectStyle = { ...fieldInput, appearance:'none', cursor:'pointer', background:'transparent' };

  return (
    <div style={{ background:'var(--bg)', color:'var(--text)', fontFamily:'DM Sans,sans-serif', minHeight:'100vh' }}>

      {/* NAV */}
      <nav style={{ position:'sticky', top:0, zIndex:100, display:'flex', alignItems:'center', justifyContent:'space-between', padding: isMobile ? '0 20px' : '0 48px', height:60, background:'var(--nav-bg)', borderBottom:'1px solid var(--border)' }}>
        <NavLogo/>
        {!isMobile && (
          <div style={{ fontFamily:'DM Mono,monospace', fontSize:11, color:'var(--muted)', letterSpacing:1 }}>
            ESPACE <span style={{ color:'var(--accent2)' }}>CANDIDAT</span>
          </div>
        )}
        {view !== 'interview' && (
          <span onClick={() => navigate('/offres')} style={{ color:'var(--muted)', fontSize:12, fontFamily:'DM Mono,monospace', cursor:'pointer', letterSpacing:0.5 }}>
            ← Retour
          </span>
        )}
        {view === 'interview' && (
          <div style={{ fontFamily:'DM Mono,monospace', fontSize:11, color:'var(--accent2)' }}>
            {progression}% complété
          </div>
        )}
      </nav>

      <div style={{ maxWidth:680, margin:'0 auto', padding: isMobile ? '24px 16px' : '48px 24px' }}>

        {/* SESSION EXISTANTE */}
        {sessionExistante && view === "upload" && (
          <div style={{ background:'rgba(232,200,61,0.08)', border:'1px solid rgba(232,200,61,0.15)', padding:'20px 24px', marginBottom:24, display:'flex', alignItems:'center', justifyContent:'space-between', gap:16, flexWrap:'wrap' }}>
            <div>
              <div style={{ fontFamily:'Syne,sans-serif', fontWeight:700, fontSize:14, marginBottom:4 }}>Entretien en cours détecté</div>
              <div style={{ fontSize:13, color:'var(--muted)' }}>Vous avez un entretien non terminé. Voulez-vous reprendre ?</div>
            </div>
            <div style={{ display:'flex', gap:10, flexShrink:0 }}>
              <button onClick={reprendreSession} style={{ background:'var(--accent)', color:'#fff', border:'none', padding:'8px 16px', fontFamily:'DM Mono,monospace', fontSize:11, letterSpacing:1, cursor:'pointer' }}>Reprendre</button>
              <button onClick={ignorerSession} style={{ background:'transparent', color:'var(--muted)', border:'1px solid var(--border)', padding:'8px 16px', fontFamily:'DM Mono,monospace', fontSize:11, letterSpacing:1, cursor:'pointer' }}>Recommencer</button>
            </div>
          </div>
        )}

        {/* UPLOAD VIEW */}
        {view === "upload" && (
          <>
            <div style={{ display:'flex', alignItems:'center', gap:20, marginBottom:40 }}>
              <div style={{ fontFamily:'Syne,sans-serif', fontSize: isMobile ? 48 : 64, fontWeight:800, lineHeight:1, color:'var(--text)' }}>02</div>
              <div>
                <div style={{ fontFamily:'DM Mono,monospace', fontSize:10, color:'var(--muted)', letterSpacing:2, textTransform:'uppercase', marginBottom:4 }}>Étape 2 sur 2</div>
                <div style={{ fontFamily:'Syne,sans-serif', fontSize: isMobile ? 18 : 20, fontWeight:800 }}>Votre CV</div>
              </div>
            </div>

            <div style={{ display:'flex', gap:4, marginBottom:40 }}>
              <div style={{ flex:1, height:2, background:'var(--accent2)' }}/>
              <div style={{ flex:1, height:2, background:'var(--accent)' }}/>
            </div>

            {!cvMode && (
              <>
                <p style={{ fontSize:15, color:'var(--muted)', marginBottom:28, lineHeight:1.7 }}>
                  Choisissez comment vous souhaitez partager votre parcours professionnel.
                </p>
                <div style={{ display:'flex', flexDirection:'column', gap:14, marginBottom:16 }}>
                  <div onClick={() => setCvMode('upload')} style={{ border:'1px solid var(--border)', padding:'24px', cursor:'pointer', background:'var(--bg2)' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:14 }}>
                      <div style={{ width:44, height:44, background:'var(--accent-08)', border:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--accent2)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                          <polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                        </svg>
                      </div>
                      <div>
                        <div style={{ fontFamily:'Syne,sans-serif', fontSize:15, fontWeight:700, marginBottom:4 }}>Uploader mon CV</div>
                        <div style={{ fontSize:14, color:'var(--muted)' }}>PDF, DOC ou DOCX — max 5 Mo</div>
                      </div>
                    </div>
                  </div>
                  <div onClick={() => setCvMode('create')} style={{ border:'1px solid var(--border)', padding:'24px', cursor:'pointer', background:'var(--bg2)' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:14 }}>
                      <div style={{ width:44, height:44, background:'var(--accent-08)', border:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--accent2)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
                        </svg>
                      </div>
                      <div>
                        <div style={{ fontFamily:'Syne,sans-serif', fontSize:15, fontWeight:700, marginBottom:4 }}>Créer mon CV</div>
                        <div style={{ fontSize:14, color:'var(--muted)' }}>Remplissez un formulaire guidé en quelques minutes</div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {cvMode === 'upload' && (
              <>
                <div onClick={() => { setCvMode(null); setFile(null); }} style={{ fontFamily:'DM Mono,monospace', fontSize:11, color:'var(--muted)', letterSpacing:1, cursor:'pointer', marginBottom:24 }}>← Changer d'option</div>
                <div style={{ border:'1px dashed var(--accent2-20)', padding:32, textAlign:'center', cursor:'pointer', background:'var(--accent2-04)', marginBottom:8 }} onClick={() => document.getElementById('cv-input').click()}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--accent2)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ margin:'0 auto 12px', display:'block' }}>
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                  </svg>
                  <div style={{ fontFamily:'DM Mono,monospace', fontSize:12, color:'var(--muted)', letterSpacing:1 }}>
                    <strong style={{ color:'var(--text)', display:'block', marginBottom:4 }}>Cliquez pour déposer</strong>
                    PDF · DOC · DOCX — max 5 Mo
                  </div>
                </div>
                <input type="file" id="cv-input" accept=".pdf,.docx" style={{ display:'none' }} onChange={e => setFile(e.target.files[0])}/>
                {file && <div style={{ fontFamily:'DM Mono,monospace', fontSize:11, color:'var(--accent2)', marginBottom:16, letterSpacing:1 }}>✓ {file.name}</div>}
              </>
            )}

            {cvMode === 'create' && (
              <>
                <div onClick={() => setCvMode(null)} style={{ fontFamily:'DM Mono,monospace', fontSize:11, color:'var(--muted)', letterSpacing:1, cursor:'pointer', marginBottom:24 }}>← Changer d'option</div>
                <div style={{ marginBottom:20 }}>
                  <label style={fieldLabel}>Expérience <span style={{ color:'var(--accent)' }}>*</span></label>
                  <select style={selectStyle} value={experience} onChange={e => setExperience(e.target.value)}>
                    <option value="">— Années —</option>
                    <option>Moins d'1 an</option>
                    <option>1 – 2 ans</option>
                    <option>3 – 5 ans</option>
                    <option>6 – 10 ans</option>
                    <option>Plus de 10 ans</option>
                  </select>
                </div>
                <div style={{ marginBottom:20 }}>
                  <label style={fieldLabel}>Formation <span style={{ color:'var(--accent)' }}>*</span></label>
                  <select style={selectStyle} value={formation} onChange={e => setFormation(e.target.value)}>
                    <option value="">— Niveau —</option>
                    <option>Bac</option>
                    <option>Bac +2 / BTS</option>
                    <option>Bac +3 / Licence</option>
                    <option>Bac +5 / Master</option>
                    <option>Doctorat</option>
                    <option>Formation professionnelle</option>
                  </select>
                </div>
                <div style={{ marginBottom:20 }}>
                  <label style={fieldLabel}>Compétences clés <span style={{ color:'var(--accent)' }}>*</span></label>
                  <input style={fieldInput} value={competences} onChange={e => setCompetences(e.target.value)} placeholder="React, Node.js, TypeScript..."/>
                </div>
                <div style={{ marginBottom:20 }}>
                  <label style={fieldLabel}>Expérience professionnelle <span style={{ color:'var(--accent)' }}>*</span></label>
                  <textarea style={{ ...fieldInput, resize:'none', minHeight:80 }} value={expPro} onChange={e => setExpPro(e.target.value)} placeholder="Décrivez vos expériences les plus pertinentes..."/>
                </div>
              </>
            )}

            {cvMode && (
              <button
                onClick={handleUpload}
                disabled={!canStart || loading}
                style={{ width:'100%', background: canStart ? 'var(--accent)' : 'var(--accent-30)', color:'#fff', border:'none', padding:'14px 24px', fontFamily:'DM Mono,monospace', fontSize:12, letterSpacing:2, textTransform:'uppercase', cursor: canStart ? 'pointer' : 'not-allowed', display:'flex', alignItems:'center', justifyContent:'center', gap:10, marginTop:24, opacity: canStart ? 1 : 0.6 }}
              >
                {loading ? 'Analyse en cours...' : "Commencer l'entretien →"}
              </button>
            )}

            {error && <div style={{ marginTop:16, fontSize:13, color:'var(--error)', fontFamily:'DM Mono,monospace', textAlign:'center' }}>{error}</div>}
          </>
        )}

        {/* INTERVIEW */}
        {view === "interview" && (
          <div style={{ background:'var(--bg2)', border:'1px solid var(--border)', padding: isMobile ? 20 : 32 }}>

            <div style={{ marginBottom:24 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
                <div style={{ fontFamily:'DM Mono,monospace', fontSize:10, color:'var(--muted)', letterSpacing:1.5, textTransform:'uppercase' }}>
                  Progression globale
                </div>
                <div style={{ fontFamily:'DM Mono,monospace', fontSize:12, color:'var(--accent2)', fontWeight:600 }}>
                  {progression}%
                </div>
              </div>
              <div style={{ height:3, background:'var(--accent-10)', borderRadius:100, overflow:'hidden' }}>
                <div style={{ height:'100%', background:'linear-gradient(90deg, var(--accent2), var(--accent))', borderRadius:100, width:`${progression}%`, transition:'width .4s ease' }}/>
              </div>
            </div>

            <div style={{ display:'flex', gap:4, marginBottom:12 }}>
              {PHASES.map((p, i) => (
                <div key={p} style={{ flex:1, textAlign:'center' }}>
                  <div style={{ height:3, borderRadius:100, marginBottom:6, background: i < phaseIndex ? 'var(--accent2)' : i === phaseIndex ? 'var(--accent)' : 'var(--accent-10)', transition:'background .4s' }}/>
                  <span style={{ fontFamily:'DM Mono,monospace', fontSize: isMobile ? 8 : 9, color: i === phaseIndex ? 'var(--accent2)' : 'var(--muted)', letterSpacing:1 }}>{PHASE_LABELS[p]}</span>
                </div>
              ))}
            </div>

            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', background:'var(--accent-08)', border:'1px solid var(--border)', padding:'10px 16px', marginBottom:24, flexWrap:'wrap', gap:8 }}>
              <span style={{ fontFamily:'DM Mono,monospace', fontSize: isMobile ? 10 : 11, color:'var(--muted)', letterSpacing:1 }}>
                Phase : <span style={{ color:'var(--text)' }}>{PHASE_LABELS[phase]}</span>
              </span>
              <span style={{ fontFamily:'DM Mono,monospace', fontSize: isMobile ? 10 : 11, color:'var(--accent2)' }}>
                Question {questionNum} / 5 — {5 - questionNum + (phaseComplete ? 1 : 0)} restante{(5 - questionNum) > 1 ? 's' : ''}
              </span>
            </div>

            <div ref={chatRef} style={{ height: isMobile ? 300 : 360, overflowY:'auto', background:'var(--bg)', padding:16, marginBottom:16, display:'flex', flexDirection:'column', gap:12 }}>
              {messages.map((m, i) => (
                <div key={i} style={{ display:'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
                  <div style={{ maxWidth: isMobile ? '90%' : '75%', padding:'12px 16px', fontSize: isMobile ? 13 : 14, lineHeight:1.6, background: m.role === 'ia' ? 'var(--accent)' : 'var(--bg3)', color:'var(--text)', borderRadius: m.role === 'ia' ? '0 12px 12px 12px' : '12px 0 12px 12px', border:'1px solid var(--border)' }}>
                    {m.content}
                  </div>
                </div>
              ))}
              {loading && (
                <div style={{ display:'flex', justifyContent:'flex-start' }}>
                  <div style={{ padding:'12px 16px', background:'var(--accent)', color:'#fff', borderRadius:'0 12px 12px 12px', fontFamily:'DM Mono,monospace', fontSize:13, letterSpacing:2 }}>...</div>
                </div>
              )}
            </div>

            {!phaseComplete ? (
              <div style={{ display:'flex', gap:8, alignItems:'flex-end' }}>
                <textarea
                  rows={1}
                  style={{ flex:1, background:'var(--bg)', border:'1px solid var(--border)', padding:'12px 16px', fontSize: isMobile ? 13 : 14, color:'var(--text)', fontFamily:'DM Sans,sans-serif', outline:'none', resize:'none', overflow:'hidden' }}
                  placeholder="Votre réponse..."
                  value={reponse}
                  onChange={e => { setReponse(e.target.value); e.target.style.height='auto'; e.target.style.height=e.target.scrollHeight+'px'; }}
                  onKeyDown={e => { if (e.key==='Enter' && !e.shiftKey) { e.preventDefault(); envoyerReponse(); }}}
                  disabled={loading}
                />
                <button
                  onClick={envoyerReponse}
                  disabled={loading || !reponse.trim()}
                  style={{ background:'var(--accent)', color:'#fff', border:'none', padding: isMobile ? '12px 14px' : '12px 20px', fontFamily:'DM Mono,monospace', fontSize:11, letterSpacing:2, textTransform:'uppercase', cursor:'pointer', opacity: reponse.trim() ? 1 : 0.5 }}
                >
                  Envoyer
                </button>
              </div>
            ) : (
              <button
                onClick={evaluerPhase}
                disabled={loading}
                style={{ width:'100%', background:'var(--success)', color:'#fff', border:'none', padding:'14px 24px', fontFamily:'DM Mono,monospace', fontSize:12, letterSpacing:2, textTransform:'uppercase', cursor:'pointer' }}
              >
                {loading ? 'Évaluation en cours...' : 'Phase suivante →'}
              </button>
            )}
          </div>
        )}

        {/* MERCI */}
        {view === "merci" && (
          <div style={{ textAlign:'center', padding: isMobile ? '40px 0' : '60px 20px' }}>
            <div style={{ fontFamily:'DM Mono,monospace', fontSize:10, color:'var(--accent2)', letterSpacing:3, textTransform:'uppercase', marginBottom:24 }}>{`// Entretien terminé`}</div>
            <div style={{ width:72, height:72, margin:'0 auto 24px', border:'1px solid rgba(22,163,74,0.2)', background:'rgba(22,163,74,0.04)', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="var(--success)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            </div>
            <div style={{ fontFamily:'Syne,sans-serif', fontSize: isMobile ? 20 : 24, fontWeight:800, marginBottom:12 }}>Merci pour votre entretien !</div>
            <div style={{ fontSize: isMobile ? 14 : 15, color:'var(--muted)', lineHeight:1.7, maxWidth:420, margin:'0 auto' }}>
              Votre dossier a été transmis à notre équipe de recrutement. Un recruteur vous contactera prochainement.
            </div>
          </div>
        )}

      </div>
    </div>
  );
}